// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useApp } from '../../store/AppContext.js';
import { formatAmount, EXPENSE_TYPES } from '../../lib/eventLog.js';

const CHART_COLORS = ['#4A7FA5', '#3D9970', '#C0392B', '#E67E22', '#6C5CE7', '#00B894'];

export function ExpenseBreakdown() {
  const { t } = useTranslation(['reports', 'common']);
  const { state } = useApp();
  const { ledger } = state;

  const now = new Date();
  const [dateFrom, setDateFrom] = useState(`${now.getFullYear()}-01-01`);
  const [dateTo, setDateTo] = useState(now.toISOString().slice(0, 10));

  const { slices, total } = useMemo(() => {
    const map = new Map<string, number>();

    for (const tx of ledger.transactions.values()) {
      if (tx.voided) continue;
      if (tx.date < dateFrom || tx.date > dateTo) continue;
      for (const split of tx.splits) {
        const account = ledger.accounts.get(split.accountId);
        if (!account || !EXPENSE_TYPES.has(account.type)) continue;
        map.set(account.name, (map.get(account.name) ?? 0) + split.amount);
      }
    }

    const slices = [...map.entries()]
      .filter(([, v]) => v > 0)
      .map(([name, amount]) => ({ name, value: amount / 100, amount }))
      .sort((a, b) => b.value - a.value);

    const total = slices.reduce((sum, s) => sum + s.amount, 0);
    return { slices, total };
  }, [ledger.transactions, ledger.accounts, dateFrom, dateTo]);

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="flex items-center gap-4 mb-4">
        <h2 className="text-md font-semibold text-text">{t('reports:titles.expenseBreakdown')}</h2>
        <div className="flex items-center gap-3 ml-auto">
          <div className="flex items-center gap-2">
            <label className="text-xs text-text-muted">{t('reports:controls.from')}</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="bg-bg border border-border text-text text-sm px-2 py-1 focus:border-accent focus:outline-none" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-text-muted">{t('reports:controls.to')}</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="bg-bg border border-border text-text text-sm px-2 py-1 focus:border-accent focus:outline-none" />
          </div>
        </div>
      </div>

      {slices.length === 0 ? (
        <div className="text-center py-16 text-text-subtle">{t('reports:empty.noExpenseData')}</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-bg-surface border border-border p-4">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  isAnimationActive={false}
                >
                  {slices.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length] ?? '#888'} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: '#222535', border: '1px solid #2A2D3A', borderRadius: 0 }}
                  itemStyle={{ color: '#7B7F9E' }}
                  labelStyle={{ color: '#E8E9F0' }}
                  formatter={(v) => [`$${Number(v).toFixed(2)}`]}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: '#7B7F9E' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-bg-surface border border-border print:bg-white print:border-gray-200">
            <div className="px-4 py-1.5 border-b border-border-strong bg-bg-elevated print:border-gray-300 print:bg-gray-50">
              <h3 className="text-sm font-semibold text-text-muted uppercase tracking-wide print:text-gray-700">{t('reports:sections.byCategory')}</h3>
            </div>
            <table className="w-full">
              <tbody>
                {slices.map((s, i) => (
                  <tr key={s.name} className="border-b border-border print:border-gray-100">
                    <td className="py-1.5 px-4">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] ?? '#888' }} />
                        <span className="text-sm text-text-muted print:text-gray-700">{s.name}</span>
                      </div>
                    </td>
                    <td className="py-1.5 px-2 text-right text-sm text-text-subtle print:text-gray-500">
                      {total > 0 ? `${((s.amount / total) * 100).toFixed(1)}%` : '—'}
                    </td>
                    <td className="py-1.5 px-4 text-right text-sm font-mono text-negative print:text-red-700">
                      {formatAmount(s.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-border-strong print:border-gray-300">
                <tr>
                  <td className="py-1.5 px-4 text-sm font-semibold text-text-muted print:text-gray-700">{t('reports:sections.total')}</td>
                  <td />
                  <td className="py-1.5 px-4 text-right text-sm font-mono font-semibold text-negative print:text-red-700">
                    {formatAmount(total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
